import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useGlobalState } from '@/state'

// Interpolating values straight into the query string sent anything the user
// typed to the server unescaped. `fetch` then normalizes the URL — space
// becomes %20, a bare % is left alone — so a tag search for "shure SM%" left
// here as `q=shure%20SM%`, which Rack refuses to unescape: a 400 before the
// controller ever ran. URLSearchParams is the platform's own form encoder and
// the exact inverse of what Rack decodes on the other end.
function createQueryString (data) {
  return new window.URLSearchParams(Object.entries(data).filter(([, value]) => value)).toString()
}

// Per-page-load identifier so multiple failures from one stuck session
// can be correlated in the server logs. Random and short — not security
// sensitive, just a correlation key.
const CLIENT_SESSION_ID = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`

// Cap reports per page load so a tight failure loop can't flood the
// server endpoint. Server has its own per-day cap as belt-and-suspenders.
let CLIENT_ERROR_REPORT_COUNT = 0
const MAX_REPORTS_PER_PAGE = 20

const useQuery = () => {
  const history = useHistory()
  const [errorNotification, setErrorNotification] = useGlobalState('errorNotification')
  const csrfToken = (document.head.querySelector('[name~=csrf-token]') || {}).content

  // Diagnostic reporter. Never throws, never blocks. Fire-and-forget POST
  // to /api/v3/client_errors/api_failure with the full context so we can
  // see *why* a fetch failed when a user gets stuck.
  //
  // `where` is the call-site bucket: 'catch' (outer fetch reject — the
  // path that triggers ErrorHandler's 504 state), 'json_parse' (inner
  // response.json() rejection, currently silent), or the HTTP status as
  // a string for explicit error branches.
  const reportApiFailure = (info) => {
    // Only report failures that put the user on the ErrorHandler page
    // (STUCK-PAGE). TOAST-only failures (5xx, 401, 403, json_parse, etc.)
    // proved to be mostly environmental noise — user keeps using the SPA,
    // not actionable for us. Watch Bugsnag / Heroku logs for those.
    if (info.triggersErrorPage !== true) return
    if (CLIENT_ERROR_REPORT_COUNT >= MAX_REPORTS_PER_PAGE) return
    CLIENT_ERROR_REPORT_COUNT += 1

    try {
      const payload = {
        session_id: CLIENT_SESSION_ID,
        where: info.where,
        // True when this failure ends up rendering ErrorHander.jsx (full-
        // page broken state) vs just toasting a notification. Each call
        // site sets this explicitly to stay accurate if branches move.
        triggers_error_page: info.triggersErrorPage === true,
        url: info.url,
        method: info.method,
        duration_ms: info.durationMs,
        error_name: info.err && info.err.name ? String(info.err.name) : '',
        error_message: info.err && info.err.message ? String(info.err.message).slice(0, 500) : '',
        response_status: info.responseStatus != null ? String(info.responseStatus) : '',
        response_url: info.responseUrl || '',
        redirected: info.redirected === true,
        online: navigator.onLine,
        effective_type: (navigator.connection && navigator.connection.effectiveType) || '',
        service_worker: !!(navigator.serviceWorker && navigator.serviceWorker.controller),
        path: window.location.pathname,
        has_csrf: !!document.head.querySelector('[name~=csrf-token]')
      }
      window.fetch('/api/v3/client_errors/api_failure', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      }).catch(() => { /* swallow — never let the reporter fail loudly */ })
    } catch (_) {
      // Belt: never let the reporter break the app.
    }
  }

  const putpostRequest = (uri, method, data, fetchCallback) => {
    // Wrapper to support a single auto-retry on transient .catch failures.
    // Most catches in practice are sub-second network blips on flaky mobile
    // connections that recover on retry — retrying once before showing the
    // ErrorHandler 504 page lets us silently absorb them.
    const doRequest = (attemptNum) => {
    const startTime = Date.now()
    window.fetch(uri, {
      method: method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(data)
    }).then((response) => {
        console.log(response)
        if(response.status === 204) { // no content, specifically from stripe's payment method call
          fetchCallback(null, response)
          return
        }
        if(response.status >= 500) {
          reportApiFailure({
            where: '5xx', triggersErrorPage: false, url: uri, method, durationMs: Date.now() - startTime,
            responseStatus: response.status, responseUrl: response.url, redirected: response.redirected
          })
          setErrorNotification('There was an error. I am ashamed of myself and I will fix it as soon as I can.')
          fetchCallback('server error')
          return
        }
        response.json().then((data) => {
        if (response.status >= 200 && response.status < 400) {
            fetchCallback(null, data)
          } else if (response.status === 404) {
            // 404s are almost always legit (deleted user, deleted credit, etc.)
            // — not a bug to triage. Skip the diagnostic report.
            //
            // Unlike getRequest, a 404 here does NOT mean "this page is gone".
            // A mutation 404 means the one thing you acted on is gone while the
            // page around it is fine — someone deleted the comment you were
            // editing, the credit you were saving. Blanking the whole page for
            // that (which is what history.replace did) loses the user's place
            // for a single stale row. Toast instead, and let the caller decide.
            setErrorNotification(data.errors || "That's gone. Someone deleted it while you were looking at it.")
            fetchCallback({}) // {} not a string: plenty of callers do Object.entries(err)
          } else if (response.status === 401) {
            reportApiFailure({
              where: '401', triggersErrorPage: false, url: uri, method, durationMs: Date.now() - startTime,
              responseStatus: response.status, responseUrl: response.url, redirected: response.redirected
            })
            setErrorNotification(data.errors || 'Unauthorized')
            fetchCallback({}) // want to make sure the promise is called
          } else if (response.status === 403) {
            reportApiFailure({
              where: '403', triggersErrorPage: false, url: uri, method, durationMs: Date.now() - startTime,
              responseStatus: response.status, responseUrl: response.url, redirected: response.redirected
            })
            setErrorNotification(data.errors || 'Forbidden')
          } else if (response.status === 429) {
            setErrorNotification(data.errors || 'Woah there! Too many requests. Please slow down.')
            fetchCallback(data.errors)
          } else if (response.status === 422) {
            if (typeof data.errors !== 'string') { // object of errors handled by js components
              fetchCallback(data.errors || 'An error occured. Please try again')
            }
            if (typeof data.errors === 'string') { // custom error handled universally
              setErrorNotification(data.errors || 'Unprocessible Entity. Please email support@castingcall.club if you need help')
              fetchCallback(data.errors || 'An error occured. Please try again')
            }
          } else {
            reportApiFailure({
              where: 'other', triggersErrorPage: false, url: uri, method, durationMs: Date.now() - startTime,
              responseStatus: response.status, responseUrl: response.url, redirected: response.redirected
            })
            if (window.bugsnagClient) {
              if (data.errors instanceof Error) {
                window.bugsnagClient.notify(data.errors)
              } else {
                window.bugsnagClient.notify(new Error(data.errors))
              }
            } else {
              console.log(data.errors)
            }
            fetchCallback(data.errors || 'An error occured. Please try again')
          }
        }).catch((parseErr) => {
          // Currently this rejection is otherwise silent (the inner
          // promise isn't returned from the outer .then, so it never
          // propagates to the outer .catch). Capture for diagnostics.
          reportApiFailure({
            where: 'json_parse', triggersErrorPage: false, url: uri, method, durationMs: Date.now() - startTime,
            err: parseErr, responseStatus: response.status, responseUrl: response.url,
            redirected: response.redirected
          })
        })
      })
      .catch((err) => {
        // First attempt: silently retry after a short delay. Most catches
        // are transient mobile-network blips and the second attempt usually
        // succeeds; the user never sees the 504 page.
        if (attemptNum === 0) {
          setTimeout(() => doRequest(1), 1500)
          return
        }
        // Browser knows it's offline — don't show the 504 page or report.
        // The page renders fine from the cached bundle; only the API is
        // unreachable. Toast and let the user keep navigating.
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          setErrorNotification('You appear to be offline. I am trying, really, but I just cant connect.')
          return
        }
        reportApiFailure({
          where: 'catch', triggersErrorPage: true, url: uri, method, durationMs: Date.now() - startTime,
          err: err
        })
        if (window.bugsnagClient) {
          if (err instanceof Error) {
            window.bugsnagClient.notify(err)
          } else {
            window.bugsnagClient.notify(new Error(err))
          }
        } else {
          console.log(err)
        }
        if (typeof history !== 'undefined') {
          history.replace(history.location.pathname, { errorStatusCode: 504 })
        }
      })
    }
    doRequest(0)
  }


  const getRequest = (uri, params, fetchCallback) => {
    // Same retry-once wrapper as putpostRequest.
    const doRequest = (attemptNum) => {
    const startTime = Date.now()
    const queryString = createQueryString(params)
    window.fetch(`${uri}?${queryString}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then((response) => {
      response.json().then((data) => {

        if (response.status >= 200 && response.status < 400) {
          fetchCallback(null, data)
        } else if (response.status === 404) {
          // TODO: Some error notifications have popups, while others re-render the whole page
          // if you use the history.replace, you could let Layouts/ErrorHandler keep all the logic in one place

          // in this line below, ErrorHandler takes care of rendering the 404
          // 404s are almost always legit (deleted user, deleted credit, etc.)
          // — not a bug to triage. Skip the diagnostic report.
          history.replace(history.location.pathname, { errorStatusCode: 404 })
        } else if (response.status === 401) {
          reportApiFailure({
            where: '401', triggersErrorPage: false, url: uri, method: 'GET', durationMs: Date.now() - startTime,
            responseStatus: response.status, responseUrl: response.url, redirected: response.redirected
          })
          setErrorNotification(data.errors || 'Unauthorized')
          fetchCallback({}) // want to make sure the promise is called
        } else if (response.status === 422) {
          setErrorNotification(data.errors || "I can't do that right now.")
        } else if (response.status === 403) {
          reportApiFailure({
            where: '403', triggersErrorPage: false, url: uri, method: 'GET', durationMs: Date.now() - startTime,
            responseStatus: response.status, responseUrl: response.url, redirected: response.redirected
          })
          setErrorNotification(data.errors || 'Forbidden')
        } else {
          reportApiFailure({
            where: response.status >= 500 ? '5xx' : 'other', triggersErrorPage: false, url: uri, method: 'GET',
            durationMs: Date.now() - startTime, responseStatus: response.status,
            responseUrl: response.url, redirected: response.redirected
          })
          if (window.bugsnagClient) {
            if (data.errors instanceof Error) {
              window.bugsnagClient.notify(data.errors)
            } else {
              window.bugsnagClient.notify(new Error(data.errors))
            }
          } else {
            console.log(data.errors)
          }
          fetchCallback(data.errors || 'An error occured. Please try again')
        }
      }).catch((parseErr) => {
        // Currently silent — inner promise not returned. Capture so we
        // can see when the server hands back HTML / non-JSON.
        reportApiFailure({
          where: 'json_parse', triggersErrorPage: false, url: uri, method: 'GET', durationMs: Date.now() - startTime,
          err: parseErr, responseStatus: response.status, responseUrl: response.url,
          redirected: response.redirected
        })
      })
    }).catch((err) => {
      // First attempt: silently retry after a short delay. Same rationale
      // as in putpostRequest — absorb transient mobile-network blips.
      if (attemptNum === 0) {
        setTimeout(() => doRequest(1), 1500)
        return
      }
      // Offline — toast and skip the 504 page + diagnostic. Not our bug.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        setErrorNotification('You appear to be offline. Are you in an elevator?')
        return
      }
      reportApiFailure({
        where: 'catch', triggersErrorPage: true, url: uri, method: 'GET', durationMs: Date.now() - startTime,
        err: err
      })
      if (window.bugsnagClient) { window.bugsnagClient.notify(err) } else { console.log(err) }
      history.replace(history.location.pathname, { errorStatusCode: 504 })
    })
    }
    doRequest(0)
  }

  return {
    getRequest,
    createQueryString,
    putpostRequest
  }
}

export default useQuery
